/**
 * The sprite panel's Motion section (spec 016): pick an entrance and when it
 * starts, or an exit and when it ends, and the button writes ordinary
 * keyframes through the store's `applySpriteMotionPreset` for every
 * orientation (D-5). One way: the fields reset after a write and never show
 * the sprite's motion back as a preset.
 */
import React, { useState } from 'react';
import { useSceneStore } from '../../../stores/sceneStore';
import { previewDurationMs } from '../../../utils/sceneDuration';
import { motionPresetKeyframes, type MotionPreset } from '../../../../../Shared/src/utils/motionPresets';
import type { Animation, Orientation, Sprite } from '../../../../../Shared/src/@types';
import styles from './PropertiesPanel.module.scss';

type Choice = { value: string; label: string; animation: Animation | null };

/** The entrances Maya can pick; Spin is not offered. */
export const ENTRANCE_CHOICES: readonly Choice[] = [
  { value: 'none', label: 'None', animation: { type: 'none' } },
  { value: 'fade', label: 'Fade', animation: { type: 'fade' } },
  { value: 'slide-left', label: 'Fly from Left', animation: { type: 'slide', direction: 'left' } },
  { value: 'slide-right', label: 'Fly from Right', animation: { type: 'slide', direction: 'right' } },
  { value: 'slide-up', label: 'Fly from Top', animation: { type: 'slide', direction: 'up' } },
  { value: 'slide-down', label: 'Fly from Bottom', animation: { type: 'slide', direction: 'down' } },
  { value: 'scale', label: 'Zoom', animation: { type: 'scale' } },
  { value: 'bounce', label: 'Bounce', animation: { type: 'bounce' } },
];

export const EXIT_CHOICES: readonly Choice[] = [
  { value: 'none', label: 'None', animation: { type: 'none' } },
  { value: 'fade', label: 'Fade', animation: { type: 'fade' } },
  { value: 'slide-left', label: 'Fly to Left', animation: { type: 'slide', direction: 'left' } },
  { value: 'slide-right', label: 'Fly to Right', animation: { type: 'slide', direction: 'right' } },
  { value: 'slide-up', label: 'Fly to Top', animation: { type: 'slide', direction: 'up' } },
  { value: 'slide-down', label: 'Fly to Bottom', animation: { type: 'slide', direction: 'down' } },
  { value: 'scale', label: 'Zoom', animation: { type: 'scale' } },
];

const DEFAULT_ENTRANCE = 'fade';
const DEFAULT_EXIT = 'fade';

/** Seconds to one decimal, as the field shows it. */
const toSeconds = (ms: number) => Math.round(Math.max(0, ms) / 100) / 10;
/** A seconds field as whole ms (≥ 0, one decimal), or null when unreadable. */
const parseSeconds = (raw: string): number | null => {
  const v = parseFloat(raw);
  if (!Number.isFinite(v)) return null;
  return Math.round(Math.max(0, v) * 10) * 100;
};

const animationOf = (choices: readonly Choice[], value: string): Animation =>
  ({ ...(choices.find((c) => c.value === value)?.animation ?? { type: 'none' }) }) as Animation;

export const MotionSection: React.FC<{ sprite: Sprite }> = ({ sprite }) => {
  const playheadPosition = useSceneStore((s) => s.playheadPosition);
  const scene = useSceneStore((s) => s.scene);
  const keyframes = useSceneStore((s) => s.keyframes);
  const previewOrientation = useSceneStore((s) => s.previewOrientation) as Orientation;

  const playLengthMs = previewDurationMs(scene, keyframes);

  // null = follow the default (the playhead, the play length).
  const [enterType, setEnterType] = useState(DEFAULT_ENTRANCE);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [exitType, setExitType] = useState(DEFAULT_EXIT);
  const [endsAt, setEndsAt] = useState<string | null>(null);

  const shownStart = startsAt ?? String(toSeconds(playheadPosition));
  const shownEnd = endsAt ?? String(toSeconds(playLengthMs));

  const write = (preset: MotionPreset) => {
    const { applySpriteMotionPreset, setPlayhead, expandedTracks, toggleTrackExpanded } = useSceneStore.getState();
    applySpriteMotionPreset(sprite.id, preset);
    // Show the result: the playhead at the start of the motion written, the track open.
    const motion = motionPresetKeyframes(sprite, preset, previewOrientation);
    if (motion.length > 0) setPlayhead(Math.min(...motion.map((k) => k.time)));
    if (!expandedTracks.has(sprite.id)) toggleTrackExpanded(sprite.id);
  };

  const addEntrance = () => {
    const atMs = parseSeconds(shownStart);
    if (atMs === null) return;
    write({ enter: { animation: animationOf(ENTRANCE_CHOICES, enterType), atMs } });
    setEnterType(DEFAULT_ENTRANCE);
    setStartsAt(null);
  };

  const addExit = () => {
    const endMs = parseSeconds(shownEnd);
    if (endMs === null) return;
    write({ exit: { animation: animationOf(EXIT_CHOICES, exitType), endMs } });
    setExitType(DEFAULT_EXIT);
    setEndsAt(null);
  };

  return (
    <div className={styles.section} data-testid="motion-section">
      <div className={styles.sectionHeader}>
        <h4>Motion</h4>
      </div>

      <div className={styles.field}>
        <label htmlFor={`entrance-${sprite.id}`}>Entrance</label>
        <select id={`entrance-${sprite.id}`} aria-label="Entrance" value={enterType} onChange={(e) => setEnterType(e.target.value)}>
          {ENTRANCE_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div className={styles.field}>
        <label htmlFor={`starts-at-${sprite.id}`}>Starts at (s)</label>
        <input
          id={`starts-at-${sprite.id}`}
          aria-label="Starts at"
          type="number"
          min={0}
          step={0.1}
          value={shownStart}
          onChange={(e) => setStartsAt(e.target.value)}
        />
      </div>
      <button type="button" onClick={addEntrance}>Add entrance</button>

      <div className={styles.field}>
        <label htmlFor={`exit-${sprite.id}`}>Exit</label>
        <select id={`exit-${sprite.id}`} aria-label="Exit" value={exitType} onChange={(e) => setExitType(e.target.value)}>
          {EXIT_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div className={styles.field}>
        <label htmlFor={`ends-at-${sprite.id}`}>Ends at (s)</label>
        <input
          id={`ends-at-${sprite.id}`}
          aria-label="Ends at"
          type="number"
          min={0}
          step={0.1}
          value={shownEnd}
          onChange={(e) => setEndsAt(e.target.value)}
        />
      </div>
      <button type="button" onClick={addExit}>Add exit</button>
    </div>
  );
};

export default MotionSection;
